import { TournamentEntity } from 'app/interfaces/entity';
import { MultiCurrencyConfig } from 'config/multi-currency-config';
import { useMemo } from 'react';
import { useDistributionBreakdown } from './use-distribution-breakdown';
import {
  useSingleCurrencyPrizeAndDistributionStatistic,
  usePrizeAndDistributionStatistic,
} from './use-prize-and-distribution-statistic';
import { useTournamentDistributionRanks } from './use-tournament-distribution-ranks';
import { useActualTournamentPlaces } from './use-tournament-places';
import { useTournamentTeamDetails } from './use-tournament-team-details';
import { useTournamentPlatformType } from './use-tournament-platform-type';
import { useTournamentLastWinningPosition } from './use-tournament-last-winning-position';

export const useTournamentCurrentPrizes = (tournament: TournamentEntity) => {
  const {
    playerPrizePool: { current: singleCurrencyPrize },
  } = useSingleCurrencyPrizeAndDistributionStatistic(tournament);

  const {
    playerPrizePool: { current: currentPrize },
  } = usePrizeAndDistributionStatistic(tournament);

  const prize = MultiCurrencyConfig.isEnabledForTournaments() ? currentPrize : singleCurrencyPrize;

  const { activeTeamsCount } = useTournamentTeamDetails(tournament);

  const { checkIsKnockoutEnabled } = useTournamentPlatformType();
  const isKnockoutTournament = checkIsKnockoutEnabled(tournament?.game?.platformType);

  const tournamentType = useMemo(
    () => tournament?.matchSetupSettings?.eliminationFormat,
    [tournament]
  );

  const lastWinningPosition = useTournamentLastWinningPosition(tournament);

  const { places } = useActualTournamentPlaces({
    lastWinningPosition,
    activeTeamsCount,
    minTeamsCount: tournament?.teamsSettings?.minTeams,
    maxTeamsCount: tournament?.teamsSettings?.maxTeams,
    tournamentType,
    isFreeTournament: prize === 0,
    payoutType: tournament?.prizeAndDistribution?.payoutType,
    thresholdPercentage: tournament.prizeAndDistribution?.thresholdPercentage,
  });

  const ranks = useTournamentDistributionRanks(places, isKnockoutTournament);

  const { distributionBreakdown } = useDistributionBreakdown({
    totalPrize: prize,
    manualWeights: tournament?.prizeAndDistribution?.manualWeights,
    places,
    ranks,
    distributionType: tournament?.prizeAndDistribution?.distributionType,
    maxTeams: tournament?.teamsSettings?.maxTeams,
  });

  return {
    currentTotalPrize: prize,
    currentDistributionBreakdown: distributionBreakdown,
  };
};
